import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useUserData } from "features/User";
import { useGroup } from "features/Group";
import { grades } from "data/grades";

export default function UserGradeLevelModal() {
  const {
    accountType,
    gradeLicenseLevel,
    userCategory,
    graduationYear,
    isSubscriptionLoading,
    isAuthenticated,
    updateGradeLevel,
  } = useUserData();
  const { group } = useGroup(false);

  const [selectedGrade, setSelectedGrade] = useState("");
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [delayOpen, setDelayOpen] = useState(true);

  const gradeLicenseLevelKey = gradeLicenseLevel?.toLowerCase();
  const isHigherEdGroup =
    group?.planType?.split("-")[1]?.toLowerCase() === "highed" ||
    accountType?.split("-")[1]?.toLowerCase() === "highed" ||
    gradeLicenseLevelKey?.includes("highed") ||
    gradeLicenseLevelKey === "highered";
  const isGovGroup =
    group?.planType?.split("-")[1]?.toLowerCase() === "gov" ||
    accountType?.split("-")[1]?.toLowerCase() === "gov" ||
    gradeLicenseLevelKey === "gov";

  const availableGrades = useMemo(() => {
    if (isHigherEdGroup) {
      return grades.filter((g) => g.value === "Educator" || g.value === "HigherEd");
    }
    return grades.filter((g) => g.value !== "HigherEd" && g.value !== "Gov");
  }, [isHigherEdGroup]);

  const shouldOpen = useMemo(() => {
    if (isSubscriptionLoading || !isAuthenticated) return false;

    const lowerCategory = userCategory?.toLowerCase();

    if (lowerCategory === "gov" || isGovGroup) return false;

    if (isHigherEdGroup) {
      return userCategory == null;
    }

    const isUserCategoryNull = userCategory == null;
    const isStudentWithNullGradYear = lowerCategory === "student" && graduationYear == null;

    return isUserCategoryNull || isStudentWithNullGradYear;

  }, [userCategory, graduationYear, isSubscriptionLoading, isAuthenticated, isHigherEdGroup, isGovGroup]);

  async function handleUpdate() {
    if (!selectedGrade) return;
    setSaving(true);
    await updateGradeLevel(selectedGrade);
    setSaving(false);
  }

  useEffect(() => {
    setIsOpen(!delayOpen && shouldOpen);
    const timer = setTimeout(() => {
      if (isAuthenticated) {
        setDelayOpen(false);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [shouldOpen, delayOpen, isAuthenticated]);

  return (
    <Dialog open={isOpen} disableEscapeKeyDown>
      <DialogTitle>Update Your Grade Level</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <DialogContentText>
            Please select your current grade level (Note: this will not affect
            your user experience.)
          </DialogContentText>
          <FormControl size="small" fullWidth>
            <InputLabel id="gradeLevelLabel">Select Grade</InputLabel>
            <Select
              labelId="gradeLevelLabel"
              label="Select Grade"
              value={selectedGrade}
              onChange={({ target }) => {
                setSelectedGrade(target.value);
              }}
            >
              {availableGrades.map((grade) => (
                <MenuItem key={grade.value} value={grade.value}>
                  {grade.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: "secondary.main",
        }}
      >
        <LoadingButton
          onClick={handleUpdate}
          loading={saving}
          variant="contained"
          disabled={!selectedGrade}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
